// QR Kiosk v2 — §8.4 citation chip + inline sentinel substitution helper. The
// chip resolves against MidasSurfaceContext: a matching page/document artifact →
// tappable terracotta chip (opens the fullscreen artifact takeover); no match →
// same geometry, dimmed + non-interactive (the reference stays legible but honest,
// no fake affordance). renderKioskText() is called by the markdown renderers
// (p/li/td/th/strong) to substitute ⟦KREF…⟧ sentinels inline. Tokens + glyphs only.
import React, { useContext } from "react";
import useTranslation from "../../../shared/hooks/useTranslation";
import { Glyph, GD } from "../shared/glyphs";
import { usePressed } from "../shared/usePressed";
import { MidasSurfaceContext } from "./midasSurfaceContext";
import { splitKioskSentinels } from "./kioskRefTags";

const chipBase: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  fontFamily: "var(--font-mono)",
  fontSize: 13, // §7.2.4 legibility bump (was 12.5)
  padding: "3px 9px", // §7.2.4 (was "2px 8px")
  borderRadius: 7,
  margin: "0 2px",
  lineHeight: 1.3,
  verticalAlign: "baseline",
};

export function KioskCite({ page, label }: { page: number; label: string }) {
  const surface = useContext(MidasSurfaceContext);
  const { translate } = useTranslation("public");
  const press = usePressed();
  const resolvable = !!surface && surface.resolvePage(page);
  const text = label || translate("kioskV2MidasArtifactPage", { page });

  if (!resolvable) {
    return (
      <span
        style={{
          ...chipBase,
          background: "var(--card-2)",
          border: "1px solid var(--border-soft)",
          color: "var(--muted)",
          cursor: "default",
        }}
      >
        <Glyph d={GD.doc} size={13} />
        {text}
      </span>
    );
  }
  return (
    <button
      type="button"
      {...press.pressHandlers}
      onClick={() => surface!.openPageArtifact(page)}
      style={{
        ...chipBase,
        fontFamily: "var(--font-mono)",
        background: "var(--midas-soft)",
        border: "1px solid var(--midas-line)",
        color: "color-mix(in oklab, var(--midas) 72%, var(--ink))",
        cursor: "pointer",
        ...press.pressStyle,
      }}
    >
      <Glyph d={GD.doc} size={12} />
      {text}
    </button>
  );
}

/** Substitute inline ⟦KREF…⟧ sentinels inside markdown-renderer children with
 *  <KioskCite> chips. Non-string children pass through untouched. */
export function renderKioskText(children: React.ReactNode): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (typeof child !== "string") return child;
    const parts = splitKioskSentinels(child);
    if (parts.length === 1 && parts[0].kind === "text") return child;
    return (
      <>
        {parts.map((p, i) =>
          p.kind === "text" ? (
            <React.Fragment key={i}>{p.text}</React.Fragment>
          ) : (
            <KioskCite key={i} page={p.page} label={p.label} />
          )
        )}
      </>
    );
  });
}

export default KioskCite;
